import { useMemo, useState } from 'react'
import { Building2, Search, X } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import { tokenManager } from '@/lib/tokenManager'
import {
  useActivateTenant,
  useChangePlan,
  useCreateTenant,
  useDeleteTenant,
  useImpersonate,
  useSuspendTenant,
  useTenants,
} from '@/features/platformAdmin/queries'
import type { PlatformTenant } from '@/features/platformAdmin/types'

const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000001'

const PLAN_LIMITS = {
  free: { aiTokens: '1,000,000', transactions: '500', seats: '1' },
  basic: { aiTokens: '10,000,000', transactions: '5,000', seats: '3' },
  standard: { aiTokens: '50,000,000', transactions: '15,000', seats: '10' },
  premium: { aiTokens: 'Unlimited', transactions: 'Unlimited', seats: '100' },
} as const

const fmtDate = (v: string | null) => (v ? formatDateTime(v) : '-')

const statusVariant = (s: string) =>
  s === 'active' ? 'success' : s === 'suspended' ? 'warning' : 'danger'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function TenantsTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const tenantsQ = useTenants(false)
  const createM = useCreateTenant()
  const planM = useChangePlan()
  const suspendM = useSuspendTenant()
  const activateM = useActivateTenant()
  const deleteM = useDeleteTenant()
  const impersonateM = useImpersonate()

  const canManage = hasPermission('admin.tenants.manage')
  const canImpersonate = hasPermission('admin.impersonate')

  const [newOpen, setNewOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [planOpen, setPlanOpen] = useState<{ tenant: PlatformTenant } | null>(null)
  const [confirm, setConfirm] = useState<{
    title: string
    body: string
    onConfirm: () => Promise<void>
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
  } | null>(null)

  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [newPlan, setNewPlan] = useState('free')
  const [newMaxUsers, setNewMaxUsers] = useState('5')
  const [newError, setNewError] = useState<string | null>(null)

  const [planChoice, setPlanChoice] = useState('free')
  const [planError, setPlanError] = useState<string | null>(null)

  const onCreate = async () => {
    setNewError(null)
    if (!newName.trim()) {
      setNewError('Name is required.')
      return
    }
    try {
      await createM.mutateAsync({
        name: newName.trim(),
        slug: newSlug.trim() || undefined,
        plan: newPlan,
        max_users: Number(newMaxUsers) || 5,
      })
      setNewOpen(false)
      setNewName('')
      setNewSlug('')
      setNewPlan('free')
      setNewMaxUsers('5')
      toast({ title: 'Tenant created.' })
    } catch (err) {
      setNewError(errMsg(err))
    }
  }

  const openPlanModal = (t: PlatformTenant) => {
    setPlanChoice(t.plan ?? 'free')
    setPlanError(null)
    setPlanOpen({ tenant: t })
  }

  const submitPlan = async () => {
    if (!planOpen) return
    setPlanError(null)
    try {
      await planM.mutateAsync({ id: planOpen.tenant.id, plan: planChoice })
      try {
        new BroadcastChannel('bokap-usage').postMessage({ type: 'refresh' })
      } catch {
        /* ignore */
      }
      setPlanOpen(null)
      toast({ title: `Plan changed to ${planChoice}.` })
    } catch (err) {
      setPlanError(errMsg(err))
    }
  }

  const requestSuspend = (t: PlatformTenant) =>
    setConfirm({
      title: 'Suspend tenant',
      body: `Suspend ${t.name}? Users will be unable to sign in.`,
      confirmLabel: 'Suspend',
      onConfirm: async () => {
        try {
          await suspendM.mutateAsync(t.id)
          toast({ title: 'Tenant suspended.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const doActivate = async (t: PlatformTenant) => {
    try {
      await activateM.mutateAsync(t.id)
      toast({ title: 'Tenant activated.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const requestDelete = (t: PlatformTenant) =>
    setConfirm({
      title: 'Delete tenant',
      body: `Soft-delete ${t.name}? Data is preserved but the tenant is hidden.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await deleteM.mutateAsync(t.id)
          toast({ title: 'Tenant deleted.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const requestImpersonate = (t: PlatformTenant) =>
    setConfirm({
      title: 'Impersonate tenant',
      body: `Impersonate ${t.name}? Audited with your real identity. 1-hour session.`,
      confirmLabel: 'Impersonate',
      onConfirm: async () => {
        try {
          const res = await impersonateM.mutateAsync(t.id)
          // Stash super-admin token to allow stop-impersonation later (matches legacy)
          try {
            const cur = tokenManager.get()
            if (cur) sessionStorage.setItem('bk.super_admin_token', cur)
          } catch {
            /* ignore */
          }
          tokenManager.set(res.token)
          toast({ title: `Impersonating ${res.tenantName}` })
          window.location.href = '/team-management'
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const columns: ColumnDef<PlatformTenant>[] = [
    {
      accessorKey: 'name',
      header: 'Workspace name',
      cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
    },
    {
      accessorKey: 'owner_name',
      header: 'Owner',
      cell: ({ row }) => row.original.owner_name ?? '-',
    },
    {
      accessorKey: 'owner_email',
      header: 'Owner email',
      cell: ({ row }) =>
        row.original.owner_email ? (
          <a className="text-primary hover:underline" href={`mailto:${row.original.owner_email}`}>
            {row.original.owner_email}
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    { accessorKey: 'plan', header: 'Plan' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
      ),
    },
    { accessorKey: 'member_count', header: 'Members' },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.created_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const t = row.original
        if (t.id === PLATFORM_TENANT_ID) {
          return <span className="text-xs text-muted-foreground">platform</span>
        }
        if (t.status === 'deleted') {
          return <span className="text-xs text-muted-foreground">deleted</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {canImpersonate && t.status === 'active' ? (
              <Button size="sm" onClick={() => requestImpersonate(t)}>
                Impersonate
              </Button>
            ) : null}
            {canManage && t.status !== 'deleted' ? (
              <Button size="sm" variant="ghost" onClick={() => openPlanModal(t)}>
                Plan
              </Button>
            ) : null}
            {canManage && t.status === 'active' ? (
              <Button size="sm" variant="ghost" onClick={() => requestSuspend(t)}>
                Suspend
              </Button>
            ) : null}
            {canManage && t.status === 'suspended' ? (
              <Button size="sm" onClick={() => doActivate(t)}>
                Activate
              </Button>
            ) : null}
            {canManage ? (
              <Button size="sm" variant="danger" onClick={() => requestDelete(t)}>
                Delete
              </Button>
            ) : null}
          </div>
        )
      },
    },
  ]

  const tenants = tenantsQ.data?.tenants ?? []
  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => {
      const haystack = [
        t.name,
        t.slug,
        t.owner_name,
        t.owner_email,
        t.plan,
        t.status,
      ]
        .filter((v): v is string => typeof v === 'string' && v.length > 0)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [tenants, search])
  const previewLimits = PLAN_LIMITS[planChoice as keyof typeof PLAN_LIMITS] ?? PLAN_LIMITS.free

  return (
    <div className="space-y-3">
      <Toolbar>
        <div className="relative w-full sm:w-80">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            placeholder="Search by name, slug, owner email…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            className="pl-9 pr-9"
          />
          {search ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {search.trim() ? (
            <span className="text-xs text-muted-foreground">
              {filteredTenants.length} of {tenants.length} match
            </span>
          ) : null}
          {canManage ? (
            <Button onClick={() => setNewOpen(true)}>
              <Building2 className="size-4" />
              New Tenant
            </Button>
          ) : null}
        </div>
      </Toolbar>

      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            rows={filteredTenants}
            loading={tenantsQ.isLoading}
            empty={search.trim() ? `No tenants match "${search.trim()}".` : 'No tenants yet.'}
          />
        </CardContent>
      </Card>

      <Modal
        open={newOpen}
        onOpenChange={setNewOpen}
        title="Create new tenant"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onCreate} disabled={createM.isPending}>
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Name"
            required
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
          />
          <FormField
            label="Slug (optional)"
            value={newSlug}
            onChange={(e) => setNewSlug(e.currentTarget.value)}
            placeholder="auto-generated if blank"
          />
          <FormField
            label="Plan"
            control={
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(PLAN_LIMITS).map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <FormField
            label="Max users"
            type="number"
            min={1}
            value={newMaxUsers}
            onChange={(e) => setNewMaxUsers(e.currentTarget.value)}
          />
          {newError ? (
            <Alert variant="destructive">
              <AlertDescription>{newError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={!!planOpen}
        onOpenChange={(v) => !v && setPlanOpen(null)}
        title={`Change plan: ${planOpen?.tenant.name ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPlanOpen(null)}>
              Cancel
            </Button>
            <Button onClick={submitPlan} disabled={planM.isPending}>
              Apply
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Plan"
            control={
              <Select value={planChoice} onValueChange={setPlanChoice}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(PLAN_LIMITS).map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <div className="rounded-md bg-muted/50 p-3 text-sm">
            <p className="mb-1 font-semibold">Applies immediately:</p>
            <ul className="space-y-0.5 text-muted-foreground">
              <li>• Tokens cap: <strong>{previewLimits.aiTokens}</strong></li>
              <li>• Transactions cap: <strong>{previewLimits.transactions}</strong></li>
              <li>• Free invited seats: <strong>{previewLimits.seats}</strong></li>
            </ul>
          </div>
          {planError ? (
            <Alert variant="destructive">
              <AlertDescription>{planError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>

      <Modal.Confirm
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'Confirm'}
        confirmVariant={confirm?.confirmVariant ?? 'primary'}
        onConfirm={async () => {
          const c = confirm
          setConfirm(null)
          if (c) await c.onConfirm()
        }}
      />
    </div>
  )
}
